import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { Button } from '@/modules/ui/components/button/Button';
import { Card, CardHeader } from '@/modules/ui/components/card/Card';
import { DeleteButton } from '@/modules/ui/components/delete-button/DeleteButton';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { UserRolesTag } from '@/modules/users/components/UserRolesTag';
import { useDeleteUserMutation } from '@/modules/users/mutations/useDeleteUserMutation';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';
import type { UserApi } from '@/modules/users/types';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface UserCardProps {
  user: UserApi;
}

export const UserCard = ({ user }: UserCardProps) => {
  const deleteUserMutation = useDeleteUserMutation();

  const currentUserQuery = useCurrentUserQuery();
  const hasUserPermission = useCheckPermissions();

  const isCurrentUser = currentUserQuery.data?.id === user.id;
  const hasUserWritePermission = hasUserPermission([permissions.Users.Update]);

  const editVisible = isCurrentUser || hasUserWritePermission;

  const handleDelete = () => {
    deleteUserMutation.mutate(user.id, {
      onSuccess: () => {
        toast.success('User has been deleted');
      },
      onError: (error) => {
        toast.error(`User has not been deleted: ${extractErrorMessage(error)}`);
      },
    });
  };

  return (
    <Card>
      <CardHeader className="flex items-center gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/users/${user.id}/edit`}>
              <Subheading className="max-w-[160px] truncate sm:max-w-[250px] md:max-w-[300px]">
                {user.firstName} {user.lastName}
              </Subheading>
            </Link>

            {user.roles && user.roles.length > 0 && (
              <UserRolesTag roles={user.roles} />
            )}
          </div>

          <Paragraph variant="caption">{user.email}</Paragraph>
        </div>

        <div className="flex flex-1 justify-end gap-2">
          {editVisible && (
            <Button variant="primary" asChild>
              <Link to={`/users/${user.id}/edit`}>Edit</Link>
            </Button>
          )}

          {!isCurrentUser && (
            <PermissionsGate permissions={[permissions.Users.Delete]}>
              <DeleteButton
                description={`This action cannot be undone. This permanently deletes the ${user.firstName} ${user.lastName}.`}
                onDelete={handleDelete}
              />
            </PermissionsGate>
          )}
        </div>
      </CardHeader>
    </Card>
  );
};
